import { useMemo } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '~/redux/hooks';
import { getProvinceMapCenterLngLat } from '~/constants/provinceCoordinates';
import MapBox from './components/MapBox';
import { useMapStatistic } from './hooks/useMapStatistic';

function ProvinceMapView() {
  const { t } = useTranslation('admin/map');
  const userProfile = useAppSelector((state) => state.auth.userProfile);
  const authUsername = useAppSelector((state) => state.auth.username);

  const mapStatisticUsername = useMemo(() => {
    const fromProfile = userProfile?.username?.trim();
    if (fromProfile) return fromProfile;
    if (typeof authUsername === 'string' && authUsername.trim()) {
      return authUsername.trim();
    }
    return null;
  }, [userProfile?.username, authUsername]);

  const rawProvinceId = userProfile?.province_id;

  const initialCenter = useMemo(() => getProvinceMapCenterLngLat(rawProvinceId ?? null), [rawProvinceId]);

  const provinceMapConfigErrorMessage = useMemo(() => {
    const hasId = rawProvinceId !== undefined && rawProvinceId !== null && String(rawProvinceId).trim() !== '';
    if (!hasId) return t('errors.provinceMapMissingProvinceId');
    if (!initialCenter) return t('errors.provinceMapUnknownProvinceCode');
    return null;
  }, [initialCenter, rawProvinceId, t]);

  const { statisticData, isLoading, errorMessage, refetch } = useMapStatistic(mapStatisticUsername);

  const mergedErrorMessage = provinceMapConfigErrorMessage ?? errorMessage ?? null;

  return (
    <Box className='h-full min-h-[360px] overflow-hidden box-border px-[22px] pt-[22px] pb-[26px]'>
      <Box className='h-full w-full min-w-0 flex flex-col'>
        <MapBox
          variant='province'
          statisticData={statisticData}
          isLoading={isLoading}
          errorMessage={mergedErrorMessage}
          onRetry={refetch}
          initialCenter={initialCenter}
        />
      </Box>
    </Box>
  );
}

export default ProvinceMapView;
